import { useClerk, useUser } from '@clerk/clerk-react';
import { css, useTheme } from '@emotion/react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { LogOut, Settings } from 'lucide-react';

import { useAdmin } from '../../hooks/use-admin';
import type { UsageData } from '../../lib/api-client';
import { createCheckoutSession, createPortalSession, fetchUsage } from '../../lib/api-client';
import { fontSizes, radii, shadows, spacing } from '../../theme/tokens';
import { Button } from '../../components/ui/button';
import { useToast } from '../ui/toast-context';

function CompactProgressBar({ value, max }: { value: number; max: number | null }) {
  const theme = useTheme();
  const percentage = max !== null && max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const atLimit = max !== null && value >= max;

  return (
    <div
      role="progressbar"
      aria-valuenow={value}
      aria-valuemax={max ?? undefined}
      css={css`
        width: 100%;
        height: 4px;
        background: ${theme.colors.border};
        border-radius: 2px;
        overflow: hidden;
      `}
    >
      {max !== null && (
        <div
          css={css`
            height: 100%;
            width: ${percentage}%;
            background: ${atLimit ? theme.colors.warning : theme.colors.primary};
            border-radius: 2px;
            transition: width 0.3s ease;
          `}
        />
      )}
    </div>
  );
}

interface UsageSectionProps {
  usage: UsageData | null;
  loading: boolean;
  error: string | null;
}

function UsageSection({ usage, loading, error }: UsageSectionProps) {
  const theme = useTheme();
  const { addToast } = useToast();

  async function handleUpgradeClick() {
    try {
      const { checkoutUrl } = await createCheckoutSession('pro');
      window.location.href = checkoutUrl;
    } catch {
      addToast({
        variant: 'error',
        title: 'Checkout failed',
        message: 'Could not start checkout. Please try again.',
      });
    }
  }

  async function handleManageClick() {
    try {
      const { portalUrl } = await createPortalSession();
      window.location.href = portalUrl;
    } catch {
      addToast({
        variant: 'error',
        title: 'Portal error',
        message: 'Could not open subscription management. Please try again.',
      });
    }
  }

  if (loading) {
    return (
      <div
        css={css`
          padding: ${spacing.sm} ${spacing.md};
          font-size: ${fontSizes.xs};
          color: ${theme.colors.textSecondary};
        `}
      >
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div
        css={css`
          padding: ${spacing.sm} ${spacing.md};
          font-size: ${fontSizes.xs};
          color: ${theme.colors.error};
        `}
      >
        {error}
      </div>
    );
  }

  if (!usage) return null; // unreachable after loading/error guards, satisfies TS

  const { projectCount, generatedMinutesThisMonth, limits } = usage;
  const isFreePlan = limits.planName === 'free';

  const projectLabel =
    limits.maxProjects !== null
      ? `${projectCount} / ${limits.maxProjects} projects`
      : `${projectCount} projects (Unlimited)`;

  const minutesValue = Number.isInteger(generatedMinutesThisMonth)
    ? generatedMinutesThisMonth
    : parseFloat(generatedMinutesThisMonth.toFixed(1));

  const minutesLabel =
    limits.maxMonthlyMinutes !== null
      ? `${minutesValue} / ${limits.maxMonthlyMinutes} min this month`
      : `${minutesValue} min this month (Unlimited)`;

  return (
    <div
      css={css`
        padding: ${spacing.sm} ${spacing.md};
        display: flex;
        flex-direction: column;
        gap: ${spacing.xs};
      `}
    >
      <span
        css={css`
          font-size: ${fontSizes.xs};
          font-weight: 600;
          color: ${theme.colors.primary};
          background: ${theme.colors.sidebarActiveBg};
          padding: 1px ${spacing.xs};
          border-radius: ${radii.sm};
          align-self: flex-start;
        `}
      >
        {limits.planDisplayName} Plan
      </span>

      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 2px;
        `}
      >
        <span
          css={css`
            font-size: ${fontSizes.xs};
            color: ${theme.colors.textSecondary};
          `}
        >
          {projectLabel}
        </span>
        <CompactProgressBar value={projectCount} max={limits.maxProjects} />
      </div>

      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 2px;
        `}
      >
        <span
          css={css`
            font-size: ${fontSizes.xs};
            color: ${theme.colors.textSecondary};
          `}
        >
          {minutesLabel}
        </span>
        <CompactProgressBar value={generatedMinutesThisMonth} max={limits.maxMonthlyMinutes} />
      </div>

      {isFreePlan ? (
        <Button
          role="menuitem"
          variant="primary"
          size="sm"
          onClick={handleUpgradeClick}
          css={css`
            margin-top: 2px;
            width: 100%;
          `}
        >
          Upgrade to Pro
        </Button>
      ) : (
        <Button
          role="menuitem"
          variant="secondary"
          size="sm"
          onClick={handleManageClick}
          css={css`
            margin-top: 2px;
            width: 100%;
          `}
        >
          Manage Subscription
        </Button>
      )}
    </div>
  );
}

interface ProfilePopoverProps {
  collapsed: boolean;
}

export function ProfilePopover({ collapsed }: ProfilePopoverProps) {
  const theme = useTheme();
  const { user } = useUser();
  const { signOut } = useClerk();
  const { isAdmin } = useAdmin();
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState<string | null>(null);
  const usageFetchedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [popoverPos, setPopoverPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
  } | null>(null);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const isNearTop = rect.top < window.innerHeight / 2;
    if (isNearTop) {
      setPopoverPos({ top: rect.bottom + 4, left: rect.right - 220 });
    } else {
      setPopoverPos({ bottom: window.innerHeight - rect.top + 4, left: rect.left });
    }
  }, [open]);

  useEffect(() => {
    if (!open || usageFetchedRef.current) return;
    usageFetchedRef.current = true;

    fetchUsage()
      .then(setUsage)
      .catch(() => setUsageError('Failed to load usage data'))
      .finally(() => setUsageLoading(false));
  }, [open]);

  const handleSignOut = useCallback(() => {
    signOut();
  }, [signOut]);

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  const displayName = user?.fullName || user?.primaryEmailAddress?.emailAddress || 'User';
  const triggerLabel = user?.firstName ?? user?.primaryEmailAddress?.emailAddress ?? 'Profile';
  const avatarInitial = (
    user?.firstName?.[0] ??
    user?.primaryEmailAddress?.emailAddress?.[0] ??
    'U'
  ).toUpperCase();
  const email = user?.primaryEmailAddress?.emailAddress || '';
  return (
    <div
      ref={containerRef}
      css={css`
        position: relative;
      `}
    >
      <button
        ref={triggerRef}
        aria-label="Profile menu"
        aria-expanded={open}
        onClick={handleToggle}
        data-testid="profile-trigger"
        css={css`
          display: flex;
          align-items: center;
          gap: ${spacing.sm};
          padding: ${spacing.xs} ${collapsed ? spacing.xs : spacing.sm};
          border-radius: ${radii.sm};
          color: ${theme.colors.sidebarText};
          cursor: pointer;
          transition: background 0.15s;
          width: 100%;
          justify-content: ${collapsed ? 'center' : 'flex-start'};

          &:hover {
            background: ${theme.colors.sidebarHoverBg};
          }
        `}
      >
        <span
          aria-hidden="true"
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: ${theme.colors.primary};
            color: ${theme.colors.surface};
            font-size: 0.75rem;
            font-weight: 600;
            flex-shrink: 0;
          `}
        >
          {avatarInitial}
        </span>
        {!collapsed && (
          <span
            css={css`
              font-size: 0.8125rem;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            `}
          >
            {triggerLabel}
          </span>
        )}
      </button>

      {open && popoverPos && (
        <div
          role="menu"
          data-testid="profile-popover"
          css={css`
            position: fixed;
            ${popoverPos.top != null
              ? `top: ${popoverPos.top}px;`
              : `bottom: ${popoverPos.bottom}px;`}
            left: ${popoverPos.left}px;
            min-width: 220px;
            background: ${theme.colors.surfaceElevated};
            border: 1px solid ${theme.colors.border};
            border-radius: ${radii.md};
            box-shadow: ${shadows.elevated};
            padding: ${spacing.xs} 0;
            z-index: 60;
          `}
        >
          {/* User info */}
          <div
            css={css`
              padding: ${spacing.sm} ${spacing.md};
              border-bottom: 1px solid ${theme.colors.border};
            `}
          >
            <div
              css={css`
                font-size: 0.8125rem;
                font-weight: 600;
                color: ${theme.colors.text};
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              `}
            >
              {displayName}
            </div>
            {email && email !== displayName && (
              <div
                css={css`
                  font-size: 0.75rem;
                  color: ${theme.colors.textSecondary};
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  margin-top: 2px;
                `}
              >
                {email}
              </div>
            )}
          </div>

          {/* Usage section */}
          <div
            css={css`
              border-bottom: 1px solid ${theme.colors.border};
            `}
          >
            <UsageSection usage={usage} loading={usageLoading} error={usageError} />
          </div>

          {/* Admin links */}
          {isAdmin && (
            <>
              <Link
                to="/app/admin/plans"
                role="menuitem"
                onClick={() => setOpen(false)}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: ${spacing.sm};
                  padding: ${spacing.sm} ${spacing.md};
                  color: ${theme.colors.text};
                  font-size: 0.8125rem;
                  cursor: pointer;
                  width: 100%;
                  text-decoration: none;
                  transition: background 0.15s;

                  &:hover {
                    background: ${theme.colors.sidebarHoverBg};
                  }
                `}
              >
                <Settings size={16} aria-hidden="true" />
                <span>Admin: Plans</span>
              </Link>
              <Link
                to="/app/admin/users"
                role="menuitem"
                onClick={() => setOpen(false)}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: ${spacing.sm};
                  padding: ${spacing.sm} ${spacing.md};
                  color: ${theme.colors.text};
                  font-size: 0.8125rem;
                  cursor: pointer;
                  width: 100%;
                  text-decoration: none;
                  transition: background 0.15s;

                  &:hover {
                    background: ${theme.colors.sidebarHoverBg};
                  }
                `}
              >
                <Settings size={16} aria-hidden="true" />
                <span>Admin: Users</span>
              </Link>
            </>
          )}

          {/* Sign out */}
          <button
            role="menuitem"
            onClick={handleSignOut}
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.sm};
              padding: ${spacing.sm} ${spacing.md};
              color: ${theme.colors.text};
              font-size: 0.8125rem;
              cursor: pointer;
              width: 100%;
              transition: background 0.15s;

              &:hover {
                background: ${theme.colors.sidebarHoverBg};
              }
            `}
          >
            <LogOut size={16} aria-hidden="true" />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}
